import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import dynamic from "next/dynamic";

const AstronautLottie = dynamic(() => import("../AstronautLottie"), {
  ssr: false,
});

interface ChatHeaderProps {
  toggleChat: () => void;
  isMobile?: boolean;
  isCompact?: boolean;
}

export default function ChatHeader({
  toggleChat,
  isMobile = false,
  isCompact = false,
}: ChatHeaderProps) {
  return (
    <div
      className="flex-shrink-0 border-b border-gray-800 bg-[#121212] text-white relative overflow-hidden transition-all"
      style={{
        borderTopLeftRadius: isMobile ? 0 : "0.5rem",
        borderTopRightRadius: isMobile ? 0 : "0.5rem",
      }}
    >
      {/* Animated stars in header background */}
      <AnimatePresence>
        {!isCompact && (
          <motion.div
            className="absolute inset-0 overflow-hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Animated nebula effect */}
            <motion.div
              className="absolute -top-10 -right-10 w-60 h-60 rounded-full bg-gradient-to-tr from-gray-900/15 via-gray-800/10 to-blue-900/10 blur-3xl"
              animate={{
                scale: [1, 1.2, 1],
                rotate: [0, 5, 0],
                opacity: [0.15, 0.2, 0.15],
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />

            {/* Star particles */}
            {[...Array(8)].map((_, i) => (
              <span
                key={i}
                className="blinking-star absolute"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  width: `${Math.random() * 2 + 1}px`,
                  height: `${Math.random() * 2 + 1}px`,
                  opacity: Math.random() * 0.7 + 0.3,
                  animationDuration: `${Math.random() * 3 + 2}s`,
                  backgroundColor: "white",
                  borderRadius: "50%",
                  boxShadow: "0 0 3px rgba(255, 255, 255, 0.5)",
                }}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Regular header content */}
      <AnimatePresence mode="wait">
        {!isCompact ? (
          <motion.div
            key="regular-header"
            className={`relative flex flex-row items-center justify-between px-4 animate-optimize py-2`}
            style={{
              height: "60px",
              backgroundColor: "#121212",
            }}
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            transition={{
              duration: 0.25,
              ease: [0.25, 0.1, 0.25, 1.0],
            }}
          >
            <div className="flex items-center relative z-10">
              <div className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-gray-800 flex items-center justify-center mr-2 overflow-hidden">
                <img
                  src="/assets/onboarding/gulu-dp.svg"
                  alt="PuStack AI"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h3 className="font-medium text-white text-sm md:text-base flex items-center">
                  PuStack AI
                  <span className="ml-2 inline-block w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></span>
                </h3>
                <p className="text-[10px] text-white/70 flex items-center">
                  <span>Your cosmic multi-tool</span>
                  <span className="ml-2 flex space-x-1">
                    {["🚀", "🔄", "🌙", "🔔"].map((emoji, i) => (
                      <span
                        key={i}
                        className="inline-block"
                        style={{
                          animation: `float ${
                            1 + i * 0.2
                          }s ease-in-out infinite alternate`,
                          fontSize: "8px",
                        }}
                      >
                        {emoji}
                      </span>
                    ))}
                  </span>
                </p>
              </div>
            </div>
            <button
              onClick={toggleChat}
              className={`${
                isMobile
                  ? "w-8 h-8 flex items-center justify-center z-50"
                  : "w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center hover:bg-gray-700 transition-colors z-50"
              }`}
            >
              {isMobile ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              )}
            </button>
          </motion.div>
        ) : (
          <motion.div
            key="compact-header"
            className={`relative flex flex-row items-center justify-between px-4 animate-optimize py-1`}
            style={{
              height: "45px",
              backgroundColor: "#121212",
            }}
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
            transition={{
              duration: 0.25,
              ease: [0.25, 0.1, 0.25, 1.0],
            }}
          >
            <div className="flex items-center relative z-10">
              <div className="w-6 h-6 rounded-full bg-gray-800 flex items-center justify-center mr-2 overflow-hidden">
                <img
                  src="/assets/onboarding/gulu-dp.svg"
                  alt="PuStack AI"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="font-medium text-white text-xs flex items-center">
                PuStack AI
                <span className="ml-2 inline-block w-1 h-1 bg-green-400 rounded-full animate-pulse"></span>
              </h3>
            </div>
            <button
              onClick={toggleChat}
              className="w-5 h-5 flex items-center justify-center z-50"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
